import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Plus, Edit, Trash2, Loader2, Search, CalendarOff, Monitor } from 'lucide-react';
import { Holiday, ClassModeSetting } from '../../types';
import { api } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

interface HolidayClassModeManagementProps {
  globalSemester?: number;
  globalAcademicYear?: string;
}

export function HolidayClassModeManagement({ globalSemester, globalAcademicYear }: HolidayClassModeManagementProps) {
  const { accessToken } = useAuth();

  // Holiday state
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [isHolidayDialogOpen, setIsHolidayDialogOpen] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState<Holiday | null>(null);
  const [holidayLoading, setHolidayLoading] = useState(true);
  const [holidaySubmitting, setHolidaySubmitting] = useState(false);
  const [filterHolidayName, setFilterHolidayName] = useState('');
  const [filterHolidayType, setFilterHolidayType] = useState('all');
  const [holidayFormData, setHolidayFormData] = useState({
    name: '',
    date: '',
    type: 'regular' as 'regular' | 'special' | 'academic',
    recurring: false,
  });

  // Class Mode state
  const [classModeSettings, setClassModeSettings] = useState<ClassModeSetting[]>([]);
  const [isClassModeDialogOpen, setIsClassModeDialogOpen] = useState(false);
  const [editingClassMode, setEditingClassMode] = useState<ClassModeSetting | null>(null);
  const [classModeLoading, setClassModeLoading] = useState(true);
  const [classModeSubmitting, setClassModeSubmitting] = useState(false);
  const [classModeFormData, setClassModeFormData] = useState({
    mode: 'face-to-face' as 'face-to-face' | 'online' | 'hybrid',
    effectiveFrom: '',
    effectiveUntil: '',
    semester: '' as string,
    schoolYear: '' as string,
    description: '',
  });

  useEffect(() => {
    fetchHolidays();
    fetchClassModeSettings();
  }, []);

  // ─── Holiday CRUD ───
  const fetchHolidays = async () => {
    try {
      setHolidayLoading(true);
      const token = accessToken || '';
      const data = await api.getHolidays(token);
      setHolidays(data.holidays);
    } catch (error) {
      console.error('Error fetching holidays:', error);
      toast.error('Failed to load holidays');
    } finally {
      setHolidayLoading(false);
    }
  };

  const handleHolidaySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setHolidaySubmitting(true);
      const token = accessToken || '';
      if (editingHoliday) {
        await api.updateHoliday(editingHoliday.id, holidayFormData, token);
        toast.success('Holiday updated successfully');
      } else {
        await api.createHoliday(holidayFormData, token);
        toast.success('Holiday created successfully');
      }
      await fetchHolidays();
      handleHolidayDialogClose();
    } catch (error) {
      console.error('Error saving holiday:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save holiday');
    } finally {
      setHolidaySubmitting(false);
    }
  };

  const handleEditHoliday = (holiday: Holiday) => {
    setEditingHoliday(holiday);
    setHolidayFormData({
      name: holiday.name,
      date: holiday.date,
      type: holiday.type,
      recurring: holiday.recurring,
    });
    setIsHolidayDialogOpen(true);
  };

  const handleDeleteHoliday = async (id: string) => {
    if (!confirm('Are you sure you want to delete this holiday?')) return;
    try {
      const token = accessToken || '';
      await api.deleteHoliday(id, token);
      toast.success('Holiday deleted successfully');
      await fetchHolidays();
    } catch (error) {
      console.error('Error deleting holiday:', error);
      toast.error('Failed to delete holiday');
    }
  };

  const handleHolidayDialogClose = () => {
    setIsHolidayDialogOpen(false);
    setEditingHoliday(null);
    setHolidayFormData({ name: '', date: '', type: 'regular', recurring: false });
  };

  // ─── Class Mode CRUD ───
  const fetchClassModeSettings = async () => {
    try {
      setClassModeLoading(true);
      const token = accessToken || '';
      const data = await api.getClassModeSettings(token);
      setClassModeSettings(data.settings);
    } catch (error) {
      console.error('Error fetching class mode settings:', error);
      toast.error('Failed to load class mode settings');
    } finally {
      setClassModeLoading(false);
    }
  };

  const handleClassModeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setClassModeSubmitting(true);
      const token = accessToken || '';
      const payload = {
        ...classModeFormData,
        effectiveUntil: classModeFormData.effectiveUntil || null,
        semester: classModeFormData.semester ? parseInt(classModeFormData.semester) : null,
        schoolYear: classModeFormData.schoolYear || null,
      };
      if (editingClassMode) {
        await api.updateClassModeSetting(editingClassMode.id, payload, token);
        toast.success('Class mode setting updated successfully');
      } else {
        await api.createClassModeSetting(payload, token);
        toast.success('Class mode setting created successfully');
      }
      await fetchClassModeSettings();
      handleClassModeDialogClose();
    } catch (error) {
      console.error('Error saving class mode setting:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to save class mode setting');
    } finally {
      setClassModeSubmitting(false);
    }
  };

  const handleEditClassMode = (setting: ClassModeSetting) => {
    setEditingClassMode(setting);
    setClassModeFormData({
      mode: setting.mode,
      effectiveFrom: setting.effectiveFrom,
      effectiveUntil: setting.effectiveUntil || '',
      semester: setting.semester != null ? setting.semester.toString() : '',
      schoolYear: setting.schoolYear || '',
      description: setting.description || '',
    });
    setIsClassModeDialogOpen(true);
  };

  const handleDeleteClassMode = async (id: string) => {
    if (!confirm('Are you sure you want to delete this class mode setting?')) return;
    try {
      const token = accessToken || '';
      await api.deleteClassModeSetting(id, token);
      toast.success('Class mode setting deleted successfully');
      await fetchClassModeSettings();
    } catch (error) {
      console.error('Error deleting class mode setting:', error);
      toast.error('Failed to delete class mode setting');
    }
  };

  const handleClassModeDialogClose = () => {
    setIsClassModeDialogOpen(false);
    setEditingClassMode(null);
    setClassModeFormData({ mode: 'face-to-face', effectiveFrom: '', effectiveUntil: '', semester: '', schoolYear: '', description: '' });
  };

  // ─── Filtered holidays ───
  const filteredHolidays = holidays.filter((h) => {
    const matchesName = !filterHolidayName || h.name.toLowerCase().includes(filterHolidayName.toLowerCase());
    const matchesType = filterHolidayType === 'all' || h.type === filterHolidayType;
    return matchesName && matchesType;
  }).sort((a, b) => a.date.localeCompare(b.date));

  const holidayTypeBadge = (type: string) => {
    switch (type) {
      case 'regular': return <Badge variant="default">Regular</Badge>;
      case 'special': return <Badge variant="secondary">Special</Badge>;
      case 'academic': return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200">Academic</Badge>;
      default: return <Badge variant="outline">{type}</Badge>;
    }
  };

  const classModeBadge = (mode: string) => {
    switch (mode) {
      case 'face-to-face': return <Badge className="bg-green-100 text-green-700 hover:bg-green-200">Face to Face</Badge>;
      case 'online': return <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-200">Online</Badge>;
      case 'hybrid': return <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-200">Hybrid</Badge>;
      default: return <Badge variant="outline">{mode}</Badge>;
    }
  };

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const semesterLabel = (sem: number | null) => {
    if (sem === 1) return '1st Semester';
    if (sem === 2) return '2nd Semester';
    if (sem === 3) return 'Summer';
    return '—';
  };

  return (
    <Tabs defaultValue="holidays" className="w-full space-y-4">
      <TabsList className="grid w-full grid-cols-2 max-w-md">
        <TabsTrigger value="holidays" className="flex items-center gap-2">
          <CalendarOff className="h-4 w-4" />
          Holidays
        </TabsTrigger>
        <TabsTrigger value="class-mode" className="flex items-center gap-2">
          <Monitor className="h-4 w-4" />
          Class Mode
        </TabsTrigger>
      </TabsList>

      {/* ═══════════ HOLIDAYS TAB ═══════════ */}
      <TabsContent value="holidays">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <CardTitle>Holiday Management</CardTitle>
                <CardDescription>Manage holidays and non-class days</CardDescription>
              </div>
              <Button onClick={() => setIsHolidayDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Holiday
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {/* Filter Controls */}
            <div className="flex flex-col sm:flex-row gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Filter by holiday name..."
                  value={filterHolidayName}
                  onChange={(e) => setFilterHolidayName(e.target.value)}
                  className="pl-8"
                />
              </div>
              <Select value={filterHolidayType} onValueChange={setFilterHolidayType}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="regular">Regular</SelectItem>
                  <SelectItem value="special">Special</SelectItem>
                  <SelectItem value="academic">Academic</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {holidayLoading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : filteredHolidays.length === 0 ? (
              <div className="text-center p-8 text-muted-foreground">
                {holidays.length === 0
                  ? 'No holidays found. Add your first holiday to get started.'
                  : 'No holidays match the current filters.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Holiday Name</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Recurring</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredHolidays.map((holiday) => (
                      <TableRow key={holiday.id}>
                        <TableCell className="font-medium">{holiday.name}</TableCell>
                        <TableCell>{formatDate(holiday.date)}</TableCell>
                        <TableCell>{holidayTypeBadge(holiday.type)}</TableCell>
                        <TableCell>
                          {holiday.recurring ? (
                            <Badge variant="outline" className="bg-amber-50 text-amber-700">Yearly</Badge>
                          ) : (
                            <Badge variant="outline">One-time</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            <Button variant="outline" size="sm" onClick={() => handleEditHoliday(holiday)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleDeleteHoliday(holiday.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Holiday Dialog */}
        <Dialog open={isHolidayDialogOpen} onOpenChange={(open) => { if (!open) handleHolidayDialogClose(); else setIsHolidayDialogOpen(true); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingHoliday ? 'Edit Holiday' : 'Add New Holiday'}</DialogTitle>
              <DialogDescription>
                {editingHoliday ? 'Update the holiday information below' : 'Fill in the details to add a new holiday'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleHolidaySubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="holidayName">Holiday Name</Label>
                <Input
                  id="holidayName"
                  value={holidayFormData.name}
                  onChange={(e) => setHolidayFormData({ ...holidayFormData, name: e.target.value })}
                  placeholder="e.g., Independence Day"
                  required
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="holidayDate">Date</Label>
                  <Input
                    id="holidayDate"
                    type="date"
                    value={holidayFormData.date}
                    onChange={(e) => setHolidayFormData({ ...holidayFormData, date: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="holidayType">Type</Label>
                  <Select
                    value={holidayFormData.type}
                    onValueChange={(v: 'regular' | 'special' | 'academic') => setHolidayFormData({ ...holidayFormData, type: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="regular">Regular Holiday</SelectItem>
                      <SelectItem value="special">Special Holiday</SelectItem>
                      <SelectItem value="academic">Academic Holiday</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Switch
                  id="recurring"
                  checked={holidayFormData.recurring}
                  onCheckedChange={(checked) => setHolidayFormData({ ...holidayFormData, recurring: checked })}
                />
                <Label htmlFor="recurring" className="text-sm">Recurring yearly</Label>
              </div>
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={handleHolidayDialogClose} disabled={holidaySubmitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={holidaySubmitting}>
                  {holidaySubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {editingHoliday ? 'Update' : 'Add'} Holiday
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </TabsContent>

      {/* ═══════════ CLASS MODE TAB ═══════════ */}
      <TabsContent value="class-mode">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <CardTitle>Mode of Class Schedule</CardTitle>
                <CardDescription>Configure whether classes are Online, Face to Face, or Hybrid</CardDescription>
              </div>
              <Button onClick={() => setIsClassModeDialogOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                Add Class Mode
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {classModeLoading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : classModeSettings.length === 0 ? (
              <div className="text-center p-8 text-muted-foreground">
                No class mode settings found. Add a setting to define how classes are held.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Mode</TableHead>
                      <TableHead>Effective From</TableHead>
                      <TableHead>Effective Until</TableHead>
                      <TableHead>Semester</TableHead>
                      <TableHead>School Year</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {classModeSettings
                      .sort((a, b) => b.effectiveFrom.localeCompare(a.effectiveFrom))
                      .map((setting) => (
                      <TableRow key={setting.id}>
                        <TableCell>{classModeBadge(setting.mode)}</TableCell>
                        <TableCell>{formatDate(setting.effectiveFrom)}</TableCell>
                        <TableCell>{setting.effectiveUntil ? formatDate(setting.effectiveUntil) : <Badge variant="outline">Ongoing</Badge>}</TableCell>
                        <TableCell>{semesterLabel(setting.semester)}</TableCell>
                        <TableCell>{setting.schoolYear || '—'}</TableCell>
                        <TableCell className="max-w-[200px] truncate">{setting.description || '—'}</TableCell>
                        <TableCell>
                          <div className="flex space-x-2">
                            <Button variant="outline" size="sm" onClick={() => handleEditClassMode(setting)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => handleDeleteClassMode(setting.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Class Mode Dialog */}
        <Dialog open={isClassModeDialogOpen} onOpenChange={(open) => { if (!open) handleClassModeDialogClose(); else setIsClassModeDialogOpen(true); }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingClassMode ? 'Edit Class Mode Setting' : 'Add Class Mode Setting'}</DialogTitle>
              <DialogDescription>
                {editingClassMode
                  ? 'Update the class mode configuration below'
                  : 'Define how classes will be conducted for a given period'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleClassModeSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="classMode">Mode</Label>
                <Select
                  value={classModeFormData.mode}
                  onValueChange={(v: 'face-to-face' | 'online' | 'hybrid') => setClassModeFormData({ ...classModeFormData, mode: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="face-to-face">Face to Face</SelectItem>
                    <SelectItem value="online">Online</SelectItem>
                    <SelectItem value="hybrid">Hybrid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="effectiveFrom">Effective From</Label>
                  <Input
                    id="effectiveFrom"
                    type="date"
                    value={classModeFormData.effectiveFrom}
                    onChange={(e) => setClassModeFormData({ ...classModeFormData, effectiveFrom: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="effectiveUntil">Effective Until (optional)</Label>
                  <Input
                    id="effectiveUntil"
                    type="date"
                    value={classModeFormData.effectiveUntil}
                    onChange={(e) => setClassModeFormData({ ...classModeFormData, effectiveUntil: e.target.value })}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="cmSemester">Semester (optional)</Label>
                  <Select
                    value={classModeFormData.semester}
                    onValueChange={(v) => setClassModeFormData({ ...classModeFormData, semester: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select semester" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">1st Semester</SelectItem>
                      <SelectItem value="2">2nd Semester</SelectItem>
                      <SelectItem value="3">Summer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cmSchoolYear">School Year (optional)</Label>
                  <Input
                    id="cmSchoolYear"
                    value={classModeFormData.schoolYear}
                    onChange={(e) => setClassModeFormData({ ...classModeFormData, schoolYear: e.target.value })}
                    placeholder="e.g., 2025-2026"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="cmDescription">Description (optional)</Label>
                <Textarea
                  id="cmDescription"
                  value={classModeFormData.description}
                  onChange={(e) => setClassModeFormData({ ...classModeFormData, description: e.target.value })}
                  placeholder="e.g., All classes shifted to online due to weather advisory"
                  rows={3}
                />
              </div>
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={handleClassModeDialogClose} disabled={classModeSubmitting}>
                  Cancel
                </Button>
                <Button type="submit" disabled={classModeSubmitting}>
                  {classModeSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  {editingClassMode ? 'Update' : 'Add'} Setting
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </TabsContent>
    </Tabs>
  );
}
